/**
 * Pannello di caricamento CSV (Anagrafica dipendenti + Costi personale
 * mensili) con storico caricamenti. Scrittura solo per ruolo admin — la UI
 * non nasconde il pannello (nessun controllo ruolo lato client in questo
 * progetto, stesso pattern delle altre sezioni di scrittura), il backend
 * rifiuta con 403 se l'utente non è admin.
 */
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { Card, ErrorBox, SimpleTable, Badge } from "../../components/ui/basics";

const ESITO_BADGE = {
  ok: "ok",
  errore_parziale: "attenzione",
  errore: "critico",
};

function FileUploadRow({ tipo, label, descrizione, onDone }) {
  const { apiRequest } = useAuth();
  const [file, setFile] = useState(null);
  const [inviando, setInviando] = useState(false);
  const [esito, setEsito] = useState(null);
  const [erroreRete, setErroreRete] = useState(null);

  async function carica(forza) {
    if (!file) return;
    setInviando(true);
    setErroreRete(null);
    setEsito(null);
    try {
      const body = new FormData();
      body.append("file", file);
      const result = await apiRequest(
        `/costo-personale/import/${tipo}${forza ? "?forza=1" : ""}`,
        { method: "POST", body },
      );
      setEsito(result);
      if (!result.giaCaricato) onDone?.();
    } catch (e) {
      setErroreRete(e.message || "Errore di rete");
    } finally {
      setInviando(false);
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <p className="font-medium text-sm text-gray-800">{label}</p>
      <p className="text-xs text-gray-500 mb-2">{descrizione}</p>
      <div className="flex items-center gap-2">
        <input
          type="file"
          accept=".csv"
          onChange={(e) => {
            setFile(e.target.files?.[0] || null);
            setEsito(null);
            setErroreRete(null);
          }}
          className="text-xs"
        />
        <button
          onClick={() => carica(false)}
          disabled={!file || inviando}
          className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white disabled:opacity-40"
        >
          {inviando ? "Carico…" : "Carica"}
        </button>
      </div>

      {erroreRete && <ErrorBox message={erroreRete} />}

      {esito?.giaCaricato && (
        <div className="mt-2 text-xs bg-yellow-50 border border-yellow-200 text-yellow-800 rounded p-2">
          <p>{esito.messaggio}</p>
          <button
            onClick={() => carica(true)}
            disabled={inviando}
            className="mt-1 underline hover:no-underline"
          >
            Carica comunque (forza rielaborazione)
          </button>
        </div>
      )}

      {esito && !esito.giaCaricato && (
        <div className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded p-2 space-y-1">
          <div className="flex items-center gap-2">
            <Badge status={ESITO_BADGE[esito.esito] || "default"}>
              {esito.esito}
            </Badge>
            <span className="text-gray-600">
              {esito.contatori.totali} righe totali — {esito.contatori.inserite}{" "}
              inserite, {esito.contatori.aggiornate} aggiornate,{" "}
              {esito.contatori.invariate} invariate, {esito.contatori.escluse}{" "}
              escluse
            </span>
          </div>
          {esito.avvisi?.length > 0 && (
            <p className="text-gray-500">
              {esito.avvisi.length} avviso/i — es. {esito.avvisi[0]}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function ImportPanel() {
  const {
    data: batches,
    loading,
    error,
    reload,
  } = useApi("/costo-personale/import/batches");

  return (
    <Card
      title="Caricamento dati"
      subtitle="Import periodico dei 2 export dal gestionale presenze — upsert su matricola, nessun duplicato su ricarichi ripetuti"
    >
      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <FileUploadRow
          tipo="anagrafica"
          label="Anagrafica dipendenti"
          descrizione="Snapshot corrente: RSA, centro di costo, mansione per dipendente"
          onDone={reload}
        />
        <FileUploadRow
          tipo="costi"
          label="Costi personale"
          descrizione="Dettaglio mensile per categoria (ordinario, straordinario, ferie, TFR…)"
          onDone={reload}
        />
      </div>

      {loading && <p className="text-xs text-gray-400">Carico storico…</p>}
      {error && <ErrorBox message={error} />}
      {batches && (
        <SimpleTable
          rowKey={(b) => b.phc_hrplnimportbatchid}
          columns={[
            {
              key: "data",
              label: "Data",
              render: (b) =>
                b.createdon
                  ? new Date(b.createdon).toLocaleString("it-IT")
                  : "—",
            },
            { key: "phc_tipofile", label: "Tipo" },
            { key: "phc_nomefile", label: "File" },
            {
              key: "phc_esito",
              label: "Esito",
              render: (b) => (
                <Badge status={ESITO_BADGE[b.phc_esito] || "default"}>
                  {b.phc_esito}
                </Badge>
              ),
            },
            {
              key: "righe",
              label: "Righe",
              align: "right",
              render: (b) =>
                `+${b.phc_righeinserite || 0} / ~${b.phc_righeaggiornate || 0} / =${b.phc_righeinvariate || 0}`,
            },
            { key: "phc_utente", label: "Utente" },
          ]}
          rows={batches}
        />
      )}
    </Card>
  );
}
